import { List, ListPlace, Place, User } from "@prisma/client";
import Fuse from "fuse.js";
import _ from "lodash";
import { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMemo, useState } from "react";
import cities from "../../../../cities.json";
import { CONTACT_EMAIL, ICON_MAP, NAV_ROUTES } from "../../../../constants";
import Avatar from "../../../../images/avatar.svg";
import ListSVG from "../../../../images/list.svg";
import {
  AppleAppStore,
  Discord,
  GooglePlay,
  Reddit,
  Twitter,
} from "../../../common/Buttons";
import Footer from "../../../common/Footer";
import { LargeLogoWithText } from "../../../common/Logo";
import {
  getPriceBadges,
  getPrivacyPolicy,
  getVibeBadges,
  RatingToThumbsIcon,
} from "../../../common/util";
import { trpc } from "../../../utils/trpc";
import { prisma } from "@gwak/db";

const getListById = (
  lists: (List & { list_place: (ListPlace & { place: Place })[] })[],
  currentListId: string | null | undefined,
) => {
  return lists.find((list) => list.id === currentListId);
};

const fuse = new Fuse(cities, {
  keys: ["city", "state"],
});

const Nav = () => {
  return (
    <section className="py-8 px-4 xl:px-10">
      <nav>
        <div className="flex items-center justify-between">
          <Link className="text-lg font-medium" href="/">
            <LargeLogoWithText />
          </Link>
          <div className="lg:hidden">
            <button className="navbar-burger flex items-center rounded p-3 hover:bg-gray-50">
              <svg
                className="block h-4 w-4"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>Mobile menu</title>
                <path d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z"></path>
              </svg>
            </button>
          </div>
          <ul className="hidden lg:ml-auto lg:mr-12 lg:flex lg:w-auto lg:items-center lg:space-x-12">
            {NAV_ROUTES.map(({ label, path }) => (
              <li key={path}>
                <Link className="text-sm font-medium" href={path}>
                  {label}
                </Link>
              </li>
            ))}
            <div className="hidden lg:flex">
              <AppleAppStore />
              <GooglePlay />
            </div>
          </ul>
        </div>
      </nav>
      <div className="navbar-menu fixed top-0 left-0 bottom-0 z-50 hidden w-5/6 max-w-sm">
        <div className="navbar-backdrop fixed inset-0 bg-gray-800 opacity-25"></div>
        <nav className="relative flex h-full w-full flex-col overflow-y-auto border-r bg-white py-6 px-6">
          <div className="mb-8 flex items-center">
            <Link
              className="mr-auto text-2xl font-medium leading-none"
              href="/"
            >
              <LargeLogoWithText />
            </Link>
            <button className="navbar-close">
              <svg
                className="h-6 w-6 cursor-pointer text-gray-500 hover:text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>
          <div>
            <ul>
              {NAV_ROUTES.map(({ label, path }) => (
                <li className="mb-1" key={path}>
                  <Link
                    className="block rounded p-4 text-sm font-medium text-gray-900 hover:bg-gray-50"
                    href={path}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-auto">
            <div className="pt-6">
              <a
                className="block rounded bg-red-400 py-3 text-center text-sm font-medium leading-normal text-white transition duration-200 hover:bg-red-300"
                href={`mailto:${CONTACT_EMAIL}`}
              >
                Contact Us
              </a>
            </div>
            <div className="mt-6 mb-4 text-center text-sm text-gray-500">
              <span>© 2022 {getPrivacyPolicy()}.</span>
            </div>
          </div>
        </nav>
      </div>
    </section>
  );
};

export default function UserProfile({
  userPlaces,
}: {
  userPlaces: {
    lists: (List & { list_place: (ListPlace & { place: Place })[] })[];
    user: User | null;
    following: number;
    followers: number;
  };
}) {
  const router = useRouter();
  const [search, setSearch] = useState("");
  const [citySearchResults, setCitySearchResults] = useState<
    Fuse.FuseResult<{
      city: string;
      growth_from_2000_to_2013: string;
      latitude: number;
      longitude: number;
      population: string;
      rank: string;
      state: string;
    }>[]
  >([]);
  const { params } = router.query;

  const debouncedSearch = useMemo(
    () =>
      _.debounce((text) => {
        const result = fuse.search(text, { limit: 5 });
        setCitySearchResults(_.uniqBy(result, "item.city"));
      }, 500),
    [],
  );

  let listId: string | null | undefined = null;
  if (params?.length) {
    if (params[0] === "lists") {
      listId = params[1];
    }
  }

  const { lists, user, followers, following } = userPlaces;

  const list = getListById(lists, listId);
  if (!user) {
    return null;
  }
  const allListPlaces = lists
    .flatMap((l) => l.list_place)
    .sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    );

  let currentListPlaces;
  if (_.isEmpty(list)) {
    currentListPlaces = allListPlaces;
  } else {
    currentListPlaces = list.list_place;
  }

  // let submit = useSubmit();

  // useEffect(() => {
  //   if (currLoc) {
  //     setSearch(currLoc);
  //   }
  // }, [currLoc]);

  const getMeta = () => {
    if (!userPlaces || !user) {
      return (
        <Head>
          <title>Profile not found</title>
          <meta name="description" content="No profile found" />
        </Head>
      );
    }
    const list = getListById(userPlaces.lists, listId);
    return (
      <Head>
        <title>{`${userPlaces.user?.username}'s ${
          list?.name || "Restaurant Recommendations"
        } | Gwak`}</title>
        <meta
          name="description"
          content={`A curated list of restaurants from ${userPlaces.user?.username}`}
        />
      </Head>
    );
  };

  const handleSearch = (text: string) => {
    setSearch(text);
    debouncedSearch(text);
  };

  const location = (
    <form
      className="relative mx-4 flex flex-col justify-center md:mx-auto md:max-w-sm"
      onKeyPress={(e) => {
        // prevent users from pressing enter
        if (e.key === "Enter") {
          e.preventDefault();
        }
      }}
    >
      <label htmlFor="location" className="mb-1">
        Enter current location:
      </label>
      <input
        name="location"
        className="rounded-md border border-slate-300 p-2 shadow-md"
        type="text"
        value={search}
        onChange={(e) => handleSearch(e.currentTarget.value)}
        placeholder="Enter a city"
      />
      <div className="absolute left-0 right-0 top-full z-30 border-2 border-b-0 border-t-0 border-slate-300">
        {citySearchResults.map(({ item }) => (
          <div
            key={`${item.city}, ${item.state}`}
            data-city={item.city}
            data-state={item.state}
            className="border-b-2 border-b-slate-300 bg-white"
            onClick={(e) => {
              const { city, state } = e.currentTarget.dataset;
              const matchingCity = cities.find(
                (c) => c.city === city && c.state === state,
              );
              setCitySearchResults([]);
              router.replace({
                query: {
                  ...router.query,
                  curr_loc: e.currentTarget.innerText,
                  lat: matchingCity?.latitude.toString() || "",
                  long: matchingCity?.longitude.toString(),
                },
              });
            }}
          >
            {item.city}, {item.state}
          </div>
        ))}
      </div>
    </form>
  );

  const cardSquares =
    currentListPlaces.length === 0 ? (
      <div className="mb-4 flex w-full" key="emtpy">
        <p className="ml-6 text-gray-500 lg:ml-4">No restaurants found!</p>
      </div>
    ) : (
      currentListPlaces.map((listPlace, idx) => {
        const { place } = listPlace;

        const queryString = new URLSearchParams({
          api: "1",
          destination:
            place.formatted_address?.replace(/(\r\n|\n|\r)/gm, " ") || "",
        }).toString();

        return (
          <div className="mb-4 flex w-full" key={listPlace.id}>
            <div className="relative flex flex-grow flex-col justify-between rounded-xl border p-6">
              <div>
                {/*<span className="absolute top-0 right-0 mt-4 mr-4 text-sm text-white px-2 py-1 font-semibold bg-gray-600 bg-opacity-50 rounded-md uppercase">*/}
                {/*  {place.place.distanceFrom} mi*/}
                {/*</span>*/}
                {/*<img*/}
                {/*  className="w-full h-full object-cover rounded-lg"*/}
                {/*  src="https://images.unsplash.com/photo-1544717297-fa95b6ee9643?ixid=MXwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHw%3D&amp;ixlib=rb-1.2.1&amp;auto=format&amp;fit=crop&amp;w=1050&amp;q=80"*/}
                {/*  alt=""*/}
                {/*/>*/}
                <div className="flex justify-between">
                  <h2 className="font-heading text-3xl font-semibold">
                    {idx + 1}. {place.name}
                  </h2>
                  <span className="text-gray-500">
                    {
                      lists.find((l) =>
                        l.list_place.some((lp) => lp.id === listPlace.id),
                      )?.name
                    }
                  </span>
                </div>
                <span className="mb-2 inline-block text-sm text-gray-400">
                  {place.street_address2
                    ? `${place.street_address} ${place.street_address2}`
                    : place.street_address}
                  , {place.city}, {place.state}
                </span>
                <div className="flex items-center">
                  {listPlace.rating == null ? null : (
                    <div className="mr-2">
                      {RatingToThumbsIcon[listPlace.rating]}
                    </div>
                  )}
                  <p className="text-sm font-light text-gray-600">
                    {new Date(listPlace.updated_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="mt-2 mb-4 leading-relaxed text-gray-800">
                  {listPlace.comments}
                  <div className="mt-2 flex flex-wrap text-sm">
                    {listPlace.vibe &&
                      typeof listPlace.vibe === "object" &&
                      getVibeBadges(listPlace.vibe)}
                    {listPlace.price &&
                      typeof listPlace.price === "object" &&
                      getPriceBadges(listPlace.price)}
                  </div>
                </div>
              </div>
              <div className="flex justify-end">
                {place.formatted_address && (
                  <a
                    className="hover: text-sm font-medium text-red-300 underline no-underline"
                    href={`https://www.google.com/maps/dir/?${queryString}`}
                  >
                    Get Directions
                  </a>
                )}
              </div>
            </div>
          </div>
        );
      })
    );

  const followStats = (
    <div className="flex flex-wrap items-center">
      <div className="w-1/2 lg:w-1/4">
        <h3 className="font-heading text-xl font-semibold">{followers}</h3>
        <p className="text-md text-gray-300">Followers</p>
      </div>
      <div className="w-1/2 lg:w-1/4">
        <h3 className="font-heading text-xl font-semibold">{following}</h3>
        <p className="text-md text-gray-300">Following</p>
      </div>
    </div>
  );

  const Tabs = () => {
    const listNotSpecified = listId === null;

    return (
      <div className="text-md border-b text-center font-medium text-gray-500">
        <ul className="-mb-px flex flex-wrap px-4 lg:px-0">
          <li>
            <Link
              href={`/profiles/${user.id}/lists`}
              className={`group inline-flex rounded-t-lg border-b-2 border-transparent py-2 px-2 hover:border-gray-300 hover:text-gray-600 dark:hover:text-gray-300 lg:py-4 ${
                listNotSpecified ? "active border-blue-600 text-blue-600" : ""
              }`}
              {...(listNotSpecified ? { "aria-current": "page" } : {})}
            >
              <Image
                className="mr-2"
                height={20}
                width={20}
                src={ICON_MAP["All"] || ""}
                alt="Lightning"
              />
              All ({allListPlaces.length})
            </Link>
          </li>
          {lists.map((l) => {
            const isActive = l.id === listId;

            return (
              <li key={l.id} className="mr-2">
                <Link
                  href={`/profiles/${user.id}/lists/${l.id}`}
                  className={`group inline-flex rounded-t-lg border-b-2 border-transparent py-2 px-2 hover:border-gray-300 hover:text-gray-600 dark:hover:text-gray-300 lg:py-4 ${
                    isActive ? "active border-blue-600 text-blue-600" : ""
                  }`}
                  {...(isActive ? { "aria-current": "page" } : {})}
                >
                  {l.name in ICON_MAP ? (
                    <Image
                      className="mr-2"
                      height={20}
                      width={20}
                      src={ICON_MAP[l.name] || ""}
                      alt=""
                    />
                  ) : (
                    <Image
                      className="mr-2"
                      height={20}
                      width={20}
                      src={ListSVG}
                      alt=""
                    />
                  )}
                  {l.name} ({l.list_place.length})
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <>
      {getMeta()}
      <Nav />
      <section className="relative mt-5">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-around">
            <div className="mx-auto w-full max-w-4xl">
              <div className="flex flex-wrap items-center">
                <div className="w-full px-4 lg:w-1/2">
                  <div className="flex justify-between">
                    <h2 className="font-heading mb-2 break-all text-3xl font-semibold md:text-4xl">
                      {user.username}
                    </h2>
                    <Image
                      className="ml-2 block h-20 rounded-xl object-contain object-top lg:ml-0 lg:hidden"
                      height={100}
                      width={100}
                      src={Avatar}
                      alt=""
                    />
                  </div>
                  <div className="container mb-6">{followStats}</div>
                  <p className="mb-4 text-xl text-gray-500">
                    {user?.profile ||
                      "This is a Gwak profile. Download the app to create your own curation today!"}
                  </p>
                  <div className="flex">
                    <Twitter />
                    <Reddit />
                    <Discord />
                  </div>
                </div>
                <div className="relative w-full lg:w-1/2 lg:px-8">
                  <div className="relative mb-10 hidden h-80 items-center lg:flex">
                    <Image
                      className="h-40 w-full rounded-xl object-contain object-center"
                      src={Avatar.src}
                      height={100}
                      width={100}
                      alt=""
                    />
                    <Image
                      className="absolute bottom-0 right-0 -mr-32"
                      src="/page-assets/icons/dots/yellow-dot-right-shield.svg"
                      height={150}
                      width={150}
                      alt=""
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="mt-10" />
      {/*{location}*/}
      <section className="">
        <div className="container mx-auto max-w-4xl">
          <Tabs />
          <div className="mt-5 flex flex-col justify-center">{cardSquares}</div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export async function getStaticPaths() {
  const users = await prisma.user.findMany({
    where: {
      is_deleted: false,
      id: {
        notIn: [
          "9ba3cb5d-8dd2-4018-ac03-0e9a4d02fc95",
          "5123ba34-3ee9-4339-9151-1f23febdb75e",
          "937bf2e8-28cd-40e9-a92a-3b69122b1cb4",
          "0e175674-8b9e-4d4e-a45e-cb2556fddf22",
          "f137307d-7cd1-4cc7-9f5d-c17573af00d9",
          "736718a7-2300-483c-aaad-884739a53ab8",
        ],
      },
      // at least one listPlace review
      list: {
        some: {
          list_place: {
            some: {},
          },
        },
      },
    },
    include: {
      list: {},
    },
  });

  const paths = users?.flatMap((user) => {
    const lists = user?.list?.map((list) => {
      return {
        params: {
          userId: user.id,
          params: ["lists", list.id.toString()],
        },
      };
    });
    return [{ params: { userId: user.id.toString(), params: [] } }, ...lists];
  });

  return {
    paths,
    fallback: "blocking", // can also be true or 'blocking'
  };
}

// `getStaticPaths` requires using `getStaticProps`
export async function getStaticProps(context: GetStaticPropsContext) {
  const { params } = context;
  const userId = typeof params?.userId === "string" ? params.userId : "";
  const lists = await prisma.list.findMany({
    where: {
      author_id: userId,
      is_deleted: false,
    },
    include: {
      list_place: {
        where: {
          place: {
            is_deleted: false,
          },
          is_deleted: false,
        },
        include: {
          place: true,
        },
        orderBy: {
          updated_at: "desc",
        },
      },
    },
  });

  const user = await prisma.user.findUnique({
    where: {
      id: userId,
    },
  });

  const following = await prisma.follower.count({
    where: {
      followee_id: userId,
    },
  });

  const followers = await prisma.follower.count({
    where: {
      user_id: userId,
    },
  });

  const userPlaces = { lists, user, following, followers };

  return {
    // Passed to the page component as props
    props: { userPlaces: JSON.parse(JSON.stringify(userPlaces)) },
    revalidate: 60, // In seconds
  };
}
